import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, InputGroup, Table } from 'react-bootstrap';
import LoadingOverlay from '../../../LoadingOverlay';
import PropertySelect from './PropertySelect';

function Properties(props) {
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingAdminProperties, setLoadingAdminProperties] = useState(false);
  const [adminProperties, setAdminProperties] = useState([]);

  /**
   * Load properties that you have admin access to when the bearer token is updated.
   */
  useEffect(() => {
    // Only bother loading properties if the user has permission to attach/detach properties
    if (props.propertyGroup.userProperty.can_edit) {
      setLoadingAdminProperties(true);
      fetch(
        `${process.env.REACT_APP_PROPERTIES_API_URL}api/properties?role=Admin`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + props.bearerToken,
            SeasonalDatabase: props.selectedDatabase
          }
        }
      )
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) props.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .then((data) => {
          if (data) {
            setAdminProperties(
              data.sort((a, b) =>
                a.name < b.name ? -1 : a.name > b.name ? 1 : 0
              )
            );
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
        })
        .finally(() => {
          setLoadingAdminProperties(false);
        });
    }
  }, [props.bearerToken]);

  /**
   * Calculated value used within the Formik form because it cannot access outside props.
   */
  const propertyGroupProperties = useMemo(
    () => props.propertyGroup.properties,
    [props.propertyGroup]
  );

  const selectAdminProperties = useMemo(
    () =>
      adminProperties
        .filter(
          (adminProperty) =>
            !propertyGroupProperties.some(
              (property) => adminProperty.id === property.id
            )
        )
        .filter((adminProperty) => {
          return adminProperty.deleted_at === null;
        }),
    [adminProperties, propertyGroupProperties]
  );

  /**
   * Detach the supplied property from the property group.
   *
   * @param {int} propertyId
   */
  const detachProperty = (propertyId) => {
    setLoadingProperties(true);
    fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups/${props.propertyGroup.id}/detach-properties?with[]=properties&with[]=users.propertyRole&append[]=userProperty`,
      {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + props.bearerToken,
          SeasonalDatabase: props.selectedDatabase
        },
        body: JSON.stringify({
          properties: [propertyId]
        })
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          props.update(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
      })
      .finally(() => {
        setLoadingProperties(false);
      });
  };

  return (
    <Card>
      <Card.Header
        style={{ height: '63px' }}
        className='d-flex align-items-center'
      >
        Properties
      </Card.Header>
      <Card.Body>
        {props.propertyGroup.userProperty.can_edit ? (
          <Formik
            initialValues={{
              properties: []
            }}
            onSubmit={(values, actions) => {
              setLoadingProperties(true);
              fetch(
                `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups/${props.propertyGroup.id}/attach-properties?with[]=properties&with[]=users.propertyRole&append[]=userProperty`,
                {
                  method: 'PUT',
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${props.bearerToken}`,
                    SeasonalDatabase: props.selectedDatabase
                  },
                  body: JSON.stringify({
                    properties: values.properties.map(
                      (property) => property.value
                    )
                  })
                }
              )
                .then((res) => {
                  if (res.ok) return res.json();
                  else if (res.status === 401) props.refreshBearerToken();
                  else throw Error(res.statusText);
                })
                .then((data) => {
                  if (data) {
                    props.update(data);
                    actions.setFieldValue('properties', []);
                  }
                })
                .catch((err) => {
                  if (process.env.NODE_ENV === 'development')
                    console.error(err);
                })
                .finally(() => {
                  setLoadingProperties(false);
                  actions.setSubmitting(false);
                });
            }}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <Form.Group controlId='properties'>
                  <InputGroup className='d-flex flex-nowrap'>
                    <PropertySelect
                      properties={props.values.properties}
                      adminProperties={selectAdminProperties}
                      loadingAdminProperties={loadingAdminProperties}
                      onChange={(options) => {
                        props.setFieldValue('properties', options || []);
                      }}
                    />
                    <InputGroup.Append>
                      <Button
                        variant='outline-success'
                        className='rounded ml-1'
                        type='submit'
                        disabled={
                          loadingAdminProperties ||
                          selectAdminProperties.length === 0 ||
                          props.values.properties.length === 0
                        }
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    </InputGroup.Append>
                  </InputGroup>
                </Form.Group>
              </Form>
            )}
          </Formik>
        ) : null}
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {props.propertyGroup.properties
              .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
              .map((property) => (
                <tr key={property.id}>
                  <td>{property.name}</td>
                  <td>
                    {props.propertyGroup.userProperty.can_edit ? (
                      <Button
                        variant='link'
                        type='button'
                        className='text-danger'
                        onClick={() => detachProperty(property.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
        {loadingProperties ? <LoadingOverlay /> : null}
      </Card.Body>
    </Card>
  );
}

Properties.propTypes = {
  // Authentication
  bearerToken: PropTypes.string.isRequired,
  selectedDatabase: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,

  // Property Group
  propertyGroup: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired
};

export default Properties;
